/**
 * DropdownSelect Component
 * 
 * A styled, reusable select dropdown input for forms. Accepts a list of 
 * options where each option uses `value` and `name` (instead of `label`).
 * 
 * Props:
 * - name (string): The name of the input field, returned in the change handler.
 * - value (string): The currently selected value.
 * - onChange (function): Callback invoked on change. Receives:
 *     {
 *       target: {
 *         name: string,
 *         value: string
 *       }
 *     }
 * - options (array): Array of option objects:
 *     [
 *       { value: string, name: string }, // `name` is shown in the UI
 *       ...
 *     ]
 * - disabled (boolean): Disables the dropdown when true.
 * - label (string): Optional text label displayed above the select input.
 * - className (string): Optional additional class for custom styling on the <select> element.
 * 
 * Example Usage:
 * 
 * const fruitOptions = [
 *   { value: "apple", name: "Apple" },
 *   { value: "banana", name: "Banana" },
 *   { value: "cherry", name: "Cherry" }
 * ];
 * 
 * <DropdownSelect
 *   name="favoriteFruit"
 *   value="banana"
 *   onChange={(e) => console.log(e.target.name, e.target.value)}
 *   options={fruitOptions}
 *   label="Select a fruit"
 *   className="custom-dropdown"
 * />
 */

import React from "react";
import styles from "./DropdownSelect.module.css";

const DropdownSelect = ({
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  label,
  className = ""
}) => {
  const combinedClassName = `${styles.dropdownSelectElement} ${className}`.trim();

  return (
    <div className={styles.dropdownSelectContainer}>
      {label && <label className={styles.dropdownSelectLabel}>{label}</label>}
      <select
        className={combinedClassName}
        name={name}
        value={value}
        onChange={(e) =>
          onChange({
            target: {
              name,
              value: e.target.value,
            },
          })
        }
        disabled={disabled}
      >
        {options.map(({ value, name }, idx) => (
          <option key={idx} value={value}>
            {name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DropdownSelect;
